import axios from 'axios';
import {
  Product,
  ProductImage,
  ProductImageCategory,
  Competitor,
  MonitoredUrl,
  ProductDocument,
  CreateProductRequest,
  UpdateProductRequest,
  CreateCompetitorRequest,
  UpdateCompetitorRequest,
  CreateMonitoredUrlRequest,
  UpdateMonitoredUrlRequest,
  UpdateProductDocumentRequest,
  ProductFilters,
  MonitoredUrlFilters,
  ProductDocumentFilters,
  DocumentSearchRequest,
  DocumentSearchResult,
  KnowledgeStats,
  BannerAssetDefaults,
  ProductImageRemovalReason,
} from '../../../types/knowledge';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
// Remove /api suffix if present to avoid duplication
const baseUrl = API_BASE_URL.replace(/\/api$/, '');
const KNOWLEDGE_API = `${baseUrl}/api/knowledge`;

// Add auth token to requests
const getAuthHeaders = () => {
  const token = localStorage.getItem('tasky_auth_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

// Helper function to transform API response to Product type
const transformProduct = (apiProduct: any): Product => {
  let productImages: ProductImage[] = [];

  // Handle product_images from Laravel relation (snake_case)
  const imagesSource = apiProduct.product_images || apiProduct.images;

  if (imagesSource) {
    try {
      const parsedImages = typeof imagesSource === 'string'
        ? JSON.parse(imagesSource)
        : imagesSource;

      if (Array.isArray(parsedImages)) {
        productImages = parsedImages.map((img: any, index: number) => ({
          id: img.id,
          product_id: apiProduct.id,
          category: img.category as ProductImageCategory,
          title: img.title || '',
          description: img.description || '',
          file_path: img.file_path || img.url || '',
          file_name: img.file_name || '',
          mime_type: img.mime_type || 'image/png',
          file_size: img.file_size || 0,
          sort_order: img.sort_order ?? index,
          is_primary: img.is_primary ?? (index === 0),
          url: img.url,
          created_at: img.created_at || '',
          updated_at: img.updated_at || '',
          logo_tone: img.logo_tone ?? null,
        }));
      }
    } catch (e) {
      console.error('Failed to parse product images:', e);
    }
  }

  // Transform competitors if present
  const competitors = apiProduct.competitors || [];

  return {
    ...apiProduct,
    product_images: productImages,
    competitors: competitors,
  };
};

class KnowledgeApiService {
  // ===== Products =====
  async getProducts(filters?: ProductFilters): Promise<Product[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.search) params.append('search', filters.search);

    const response = await axios.get(`${KNOWLEDGE_API}/products?${params}`, {
      headers: getAuthHeaders(),
    });

    // Transform each product to include product_images array
    return (response.data.data || []).map(transformProduct);
  }

  async getProduct(id: number): Promise<Product> {
    const response = await axios.get(`${KNOWLEDGE_API}/products/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  }

  /** バナー作成の商品ピッカーの初期選択。MCP の推薦・生成と同じ素材の役割判定を返す */
  async getBannerAssetDefaults(productId: number): Promise<BannerAssetDefaults> {
    const response = await axios.get(`${KNOWLEDGE_API}/products/${productId}/banner-asset-defaults`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  }

  async createProduct(data: CreateProductRequest): Promise<Product> {
    const response = await axios.post(`${KNOWLEDGE_API}/products`, data, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  }

  async updateProduct(id: number, data: UpdateProductRequest): Promise<Product> {
    const response = await axios.put(`${KNOWLEDGE_API}/products/${id}`, data, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  }

  async deleteProduct(id: number): Promise<void> {
    await axios.delete(`${KNOWLEDGE_API}/products/${id}`, {
      headers: getAuthHeaders(),
    });
  }

  // ===== Competitors =====
  async getCompetitors(productId: number): Promise<Competitor[]> {
    const response = await axios.get(`${KNOWLEDGE_API}/products/${productId}/competitors`, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  }

  async rediscoverCompetitors(productId: number): Promise<{ message: string; fetch_progress?: any }> {
    const response = await axios.post(
      `${KNOWLEDGE_API}/products/${productId}/rediscover-competitors`,
      {},
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  }

  async getCompetitor(productId: number, competitorId: number): Promise<Competitor> {
    const response = await axios.get(
      `${KNOWLEDGE_API}/products/${productId}/competitors/${competitorId}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  }

  async createCompetitor(productId: number, data: CreateCompetitorRequest): Promise<Competitor> {
    const response = await axios.post(
      `${KNOWLEDGE_API}/products/${productId}/competitors`,
      data,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  }

  async updateCompetitor(
    productId: number,
    competitorId: number,
    data: UpdateCompetitorRequest
  ): Promise<Competitor> {
    const response = await axios.put(
      `${KNOWLEDGE_API}/products/${productId}/competitors/${competitorId}`,
      data,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  }

  async deleteCompetitor(productId: number, competitorId: number): Promise<void> {
    await axios.delete(
      `${KNOWLEDGE_API}/products/${productId}/competitors/${competitorId}`,
      {
        headers: getAuthHeaders(),
      }
    );
  }

  // ===== Monitored URLs =====
  async getMonitoredUrls(
    competitorId: number,
    filters?: MonitoredUrlFilters
  ): Promise<MonitoredUrl[]> {
    const params = new URLSearchParams();
    if (filters?.active !== undefined) params.append('active', filters.active.toString());
    if (filters?.page_type) params.append('page_type', filters.page_type);
    if (filters?.status) params.append('status', filters.status);

    const response = await axios.get(
      `${KNOWLEDGE_API}/competitors/${competitorId}/monitored-urls?${params}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  }

  async getMonitoredUrl(competitorId: number, urlId: number): Promise<MonitoredUrl> {
    const response = await axios.get(
      `${KNOWLEDGE_API}/competitors/${competitorId}/monitored-urls/${urlId}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  }

  async createMonitoredUrl(
    competitorId: number,
    data: CreateMonitoredUrlRequest
  ): Promise<MonitoredUrl> {
    const response = await axios.post(
      `${KNOWLEDGE_API}/competitors/${competitorId}/monitored-urls`,
      data,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  }

  async updateMonitoredUrl(
    competitorId: number,
    urlId: number,
    data: UpdateMonitoredUrlRequest
  ): Promise<MonitoredUrl> {
    const response = await axios.put(
      `${KNOWLEDGE_API}/competitors/${competitorId}/monitored-urls/${urlId}`,
      data,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  }

  async deleteMonitoredUrl(competitorId: number, urlId: number): Promise<void> {
    await axios.delete(
      `${KNOWLEDGE_API}/competitors/${competitorId}/monitored-urls/${urlId}`,
      {
        headers: getAuthHeaders(),
      }
    );
  }

  async checkMonitoredUrl(competitorId: number, urlId: number): Promise<MonitoredUrl> {
    const response = await axios.post(
      `${KNOWLEDGE_API}/competitors/${competitorId}/monitored-urls/${urlId}/check`,
      {},
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  }

  // ===== Product Documents =====
  async getDocuments(productId: number, filters?: ProductDocumentFilters): Promise<ProductDocument[]> {
    const params = new URLSearchParams();
    if (filters?.file_type) params.append('file_type', filters.file_type);
    if (filters?.source_type) params.append('source_type', filters.source_type);

    const response = await axios.get(
      `${KNOWLEDGE_API}/products/${productId}/documents?${params}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  }

  async getDocument(productId: number, documentId: number): Promise<ProductDocument> {
    const response = await axios.get(
      `${KNOWLEDGE_API}/products/${productId}/documents/${documentId}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  }

  async uploadDocument(productId: number, file: File, description?: string): Promise<ProductDocument> {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    const token = localStorage.getItem('tasky_auth_token');
    const response = await axios.post(
      `${KNOWLEDGE_API}/products/${productId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : '',
        },
      }
    );
    return response.data.data;
  }

  async updateDocument(
    productId: number,
    documentId: number,
    data: UpdateProductDocumentRequest
  ): Promise<ProductDocument> {
    const response = await axios.patch(
      `${KNOWLEDGE_API}/products/${productId}/documents/${documentId}`,
      data,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  }

  async deleteDocument(productId: number, documentId: number): Promise<void> {
    await axios.delete(
      `${KNOWLEDGE_API}/products/${productId}/documents/${documentId}`,
      {
        headers: getAuthHeaders(),
      }
    );
  }

  async getDocumentDownloadUrl(productId: number, documentId: number): Promise<{ url: string; expires_in: number }> {
    const response = await axios.get(
      `${KNOWLEDGE_API}/products/${productId}/documents/${documentId}/download`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  }

  async getDocumentPreviewUrl(productId: number, documentId: number): Promise<{ url: string; expires_in: number }> {
    const response = await axios.get(
      `${KNOWLEDGE_API}/products/${productId}/documents/${documentId}/preview`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  }


  async getDocumentTextContent(productId: number, documentId: number): Promise<{ text_content: string | null; has_content: boolean }> {
    const response = await axios.get(
      `${KNOWLEDGE_API}/products/${productId}/documents/${documentId}/text-content`,
      { headers: getAuthHeaders() }
    );
    return response.data.data;
  }

  async toggleDocumentAIContext(productId: number, documentId: number): Promise<ProductDocument> {
    const response = await axios.post(
      `${KNOWLEDGE_API}/products/${productId}/documents/${documentId}/toggle-ai-context`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data.data;
  }

  async getProductAIContext(productId: number): Promise<{ context: string; has_context: boolean; character_count: number }> {
    const response = await axios.get(
      `${KNOWLEDGE_API}/products/${productId}/ai-context`,
      { headers: getAuthHeaders() }
    );
    return response.data.data;
  }

  async uploadDocumentWithProgress(
    productId: number,
    file: File,
    description?: string,
    onProgress?: (percent: number) => void
  ): Promise<ProductDocument> {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);

    const token = localStorage.getItem('tasky_auth_token');
    const response = await axios.post(
      `${KNOWLEDGE_API}/products/${productId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percent);
          }
        },
      }
    );
    return response.data.data;
  }

  async searchDocuments(productId: number, searchRequest: DocumentSearchRequest): Promise<DocumentSearchResult> {
    const response = await axios.post(
      `${KNOWLEDGE_API}/products/${productId}/documents/search`,
      searchRequest,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  }

  // ===== Dashboard Stats =====
  async getStats(): Promise<KnowledgeStats> {
    // TODO: Implement stats endpoint on backend
    // For now, return mock data
    return {
      total_products: 0,
      total_competitors: 0,
      total_monitored_urls: 0,
      active_monitoring: 0,
      recent_changes: 0,
      failed_checks: 0,
    };
  }

  // ===== Product Images =====
  async getProductImages(productId: number, category?: ProductImageCategory): Promise<ProductImage[]> {
    const params = new URLSearchParams();
    if (category) params.append('category', category);

    const response = await axios.get(`${KNOWLEDGE_API}/products/${productId}/images?${params}`, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  }

  async uploadProductImage(
    productId: number,
    file: File,
    category?: ProductImageCategory,
    title?: string,
    description?: string,
    isPrimary?: boolean
  ): Promise<ProductImage> {
    const formData = new FormData();
    formData.append('image', file);
    if (category) formData.append('category', category);
    if (title) formData.append('title', title);
    if (description) formData.append('description', description);
    if (isPrimary !== undefined) formData.append('is_primary', isPrimary ? '1' : '0');

    const token = localStorage.getItem('tasky_auth_token');
    const response = await axios.post(`${KNOWLEDGE_API}/products/${productId}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });
    return response.data.data;
  }

  async updateProductImage(
    productId: number,
    imageId: number,
    data: { title?: string; description?: string; is_primary?: boolean; removal_suggestion?: ProductImageRemovalReason | null }
  ): Promise<ProductImage> {
    const response = await axios.put(
      `${KNOWLEDGE_API}/products/${productId}/images/${imageId}`,
      data,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  }

  async deleteProductImage(productId: number, imageId: number): Promise<void> {
    await axios.delete(`${KNOWLEDGE_API}/products/${productId}/images/${imageId}`, {
      headers: getAuthHeaders(),
    });
  }

  async reorderProductImages(productId: number, imageIds: number[]): Promise<ProductImage[]> {
    const response = await axios.post(
      `${KNOWLEDGE_API}/products/${productId}/images/reorder`,
      { image_ids: imageIds },
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  }

  async setProductImagePrimary(productId: number, imageId: number): Promise<ProductImage> {
    const response = await axios.post(
      `${KNOWLEDGE_API}/products/${productId}/images/${imageId}/primary`,
      {},
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  }

  async uploadProductImageFromUrl(
    productId: number,
    url: string,
    category?: ProductImageCategory,
    title?: string,
    description?: string
  ): Promise<ProductImage> {
    const response = await axios.post(
      `${KNOWLEDGE_API}/products/${productId}/images/url`,
      {
        url,
        category,
        title,
        description,
      },
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  }
}

export default new KnowledgeApiService();
